import React from "react";
import { BellRing, BellOff, Send, Loader2 } from "lucide-react";
import { apiPushKunci, apiPushLangganan, apiPushBerhenti, apiPushUji, formatApiErrorDetail } from "@/lib/api";
import { kunciKeBytes, statusPush } from "@/lib/pushUtil";

const didukung = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window
  && "Notification" in window && window.isSecureContext;

// G1-D: switch push notifications on/off for THIS device (owner only).
export default function PushPanel({ testid = "push-panel" }) {
  const [st, setSt] = React.useState({ didukung: didukung(), izin: didukung() ? Notification.permission : "default", berlangganan: false, mode: null });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const cek = React.useCallback(async () => {
    if (!didukung()) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const k = await apiPushKunci();
      setSt({ didukung: true, izin: Notification.permission, berlangganan: !!sub, mode: k.mode });
    } catch { /* keep current state */ }
  }, []);
  React.useEffect(() => { cek(); }, [cek]);

  const jalankan = async (fn) => {
    setBusy(true);
    setMsg(null);
    try { await fn(); } catch (e) { setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); cek(); }
  };

  const aktifkan = () => jalankan(async () => {
    const k = await apiPushKunci();
    if (!k.public_key) throw new Error("Server belum punya kunci VAPID (mode simulasi). Hubungi pengelola.");
    const izin = await Notification.requestPermission();
    if (izin !== "granted") throw new Error("Izin notifikasi tidak diberikan.");
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: kunciKeBytes(k.public_key) });
    const j = sub.toJSON();
    await apiPushLangganan({ endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } });
    setMsg("Notifikasi HP aktif.");
  });
  const matikan = () => jalankan(async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiPushBerhenti(sub.endpoint).catch(() => {});
      await sub.unsubscribe();
    }
    setMsg("Notifikasi HP dimatikan di perangkat ini.");
  });
  const uji = () => jalankan(async () => {
    const r = await apiPushUji();
    setMsg(r.mode === "simulasi" ? "Uji dicatat (mode simulasi, tidak dikirim)." : "Uji dikirim — cek notifikasi di perangkat.");
  });

  const s = statusPush(st);
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm text-slate-200">
        {s.kode === "AKTIF" ? <BellRing size={16} className="text-emerald-400" /> : <BellOff size={16} className="text-slate-500" />}
        <span data-testid={`${testid}-status`} data-kode={s.kode}>{s.teks}</span>
      </div>
      <p className="text-xs text-slate-500">Isi notifikasi di layar kunci tanpa nama customer dan nominal. Jam tenang mengikuti Pengaturan → Notifikasi.</p>
      <div className="flex flex-wrap gap-2">
        {st.didukung && !st.berlangganan && st.izin !== "denied" && (
          <button type="button" disabled={busy} onClick={aktifkan} data-testid={`${testid}-aktifkan`}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Aktifkan notifikasi di perangkat ini
          </button>
        )}
        {st.berlangganan && (
          <>
            <button type="button" disabled={busy} onClick={uji} data-testid={`${testid}-uji`}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200"><Send size={14} /> Kirim uji</button>
            <button type="button" disabled={busy} onClick={matikan} data-testid={`${testid}-matikan`}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-400"><BellOff size={14} /> Matikan</button>
          </>
        )}
      </div>
      {msg && <p data-testid={`${testid}-msg`} className="text-xs text-slate-300">{msg}</p>}
    </div>
  );
}
